import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Trophy, Gift, Users, LogOut, Shield, Sparkles, Zap, Languages, Share2, Baby, UserCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { toast } from "sonner";

const NavItem = ({ to, icon: Icon, label, testId }) => {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-1 flex-1 py-2 transition-all ${
        active ? "text-gold" : "text-ink-500"
      }`}
    >
      <Icon size={active ? 24 : 20} strokeWidth={2.4} />
      <span className="text-[10px] font-bold">{label}</span>
    </Link>
  );
};

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();

  const toggleKids = async () => {
    try {
      const next = !user?.kids_mode;
      await api.post(`/auth/kids-mode?enabled=${next}`);
      await refresh();
      toast.success(next ? "Modalità Bambini attivata 🧒" : "Modalità standard");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div className={`min-h-screen flex flex-col bg-parchment-grain ${user?.kids_mode ? "kids-mode" : ""}`}>
      <header className="sticky top-0 z-30 backdrop-blur bg-parchment/90 border-b border-outline">
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" aria-label="Odismap — Home">
            <img
              src="/odismap-logo-transparent.png"
              alt="Odismap — Caccia al tesoro"
              className="h-12 sm:h-14 w-auto object-contain"
              data-testid="brand-logo"
              draggable={false}
            />
          </Link>
          <div className="flex items-center gap-2">
            {user && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl border-outline hidden sm:inline-flex"
                onClick={() => navigate("/referral")}
                data-testid="nav-referral"
              >
                <Share2 size={16} className="mr-1"/> Invita
              </Button>
            )}
            {user?.role === "admin" && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl border-outline"
                onClick={() => navigate("/admin")}
                data-testid="nav-admin"
              >
                <Shield size={16} className="mr-1" /> {t("admin")}
              </Button>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gold-light border border-gold/40 hover:bg-gold hover:text-ink-900 transition-colors"
                  data-testid="header-profile-btn"
                  title="Vai al profilo"
                >
                  <Sparkles size={14} className="text-gold-hover" />
                  <span className="text-sm font-bold text-ink-800" data-testid="user-points">{user.total_points} pt</span>
                </button>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
                  <LogOut size={18} />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                className="btn-tactile !py-2 !pl-3 !pr-5 !rounded-full flex items-center gap-2"
                data-testid="header-login-btn"
              >
                <UserCircle2 size={18} strokeWidth={2.4}/> {t("login")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 pb-24">
        <Outlet />
      </main>

      {user && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-outline shadow-[0_-4px_20px_rgba(62,39,35,0.08)]" data-testid="bottom-nav">
          <div className="max-w-5xl mx-auto flex items-stretch px-2">
            <NavItem to="/" icon={Home} label={t("explore")} testId="nav-home" />
            <NavItem to="/leaderboard" icon={Trophy} label={t("leaderboard")} testId="nav-leaderboard" />
            <NavItem to="/rewards" icon={Gift} label={t("rewards")} testId="nav-rewards" />
          </div>
        </nav>
      )}
    </div>
  );
}
